/**
 * Kea 3.x compatibility shim for class components.
 *
 * In Kea 0.28.x, @connect({ actions: [logic, ['actionA']], props: [logic, ['propA']] })
 * was a React HOC that wired a class component to the redux store and auto-mounted
 * the referenced logic.
 *
 * In Kea 3.x, connect() is only for logic-to-logic connections — there is no
 * component HOC, and a logic must be explicitly mounted before its selectors work.
 *
 * This shim recreates the old behavior:
 *  - mounts each referenced logic for the component's lifetime (so selectors resolve)
 *  - maps selectors -> props and action creators -> props.actions (and top-level)
 */
import React, { Component } from 'react'
import { connect as reduxConnect } from 'react-redux'

function buildLogic (logic) {
  if (!logic) return null
  if (typeof logic === 'function' && typeof logic.build === 'function') {
    return logic.build()
  }
  return logic
}

function parseKey (keyExpr) {
  return keyExpr.includes(' as ')
    ? keyExpr.split(' as ').map((s) => s.trim())
    : [keyExpr, keyExpr]
}

function collectLogics (...mappings) {
  const logics = []
  for (const mapping of mappings) {
    if (!mapping || !mapping.length) continue
    for (let i = 0; i < mapping.length; i += 2) {
      const logic = mapping[i]
      if (logic && logics.indexOf(logic) === -1) {
        logics.push(logic)
      }
    }
  }
  return logics
}

/**
 * Build a mapStateToProps from the Kea-style props/values mapping.
 * Mapping format: [logic, ['selectorA', 'selectorB as alias']]
 */
function buildMapState (mapping) {
  if (!mapping || !mapping.length) return null
  return function (state) {
    const result = {}
    for (let i = 0; i < mapping.length; i += 2) {
      const builtLogic = buildLogic(mapping[i])
      const keys = mapping[i + 1]
      if (!builtLogic || !builtLogic.selectors || !keys) continue
      for (const keyExpr of keys) {
        const [from, alias] = parseKey(keyExpr)
        const selector = builtLogic.selectors[from]
        if (selector) {
          try {
            result[alias] = selector(state, builtLogic.props || {})
          } catch (e) {
            result[alias] = undefined
          }
        }
      }
    }
    return result
  }
}

/**
 * Build a mapDispatchToProps from the Kea-style actions mapping.
 * Mapping format: [logic, ['actionA', 'actionB as alias']]
 */
function buildMapDispatch (mapping) {
  if (!mapping || !mapping.length) return null
  return function (dispatch) {
    const dispatchers = {}
    for (let i = 0; i < mapping.length; i += 2) {
      const builtLogic = buildLogic(mapping[i])
      const keys = mapping[i + 1]
      if (!builtLogic || !builtLogic.actionCreators || !keys) continue
      for (const keyExpr of keys) {
        const [from, alias] = parseKey(keyExpr)
        const creator = builtLogic.actionCreators[from]
        if (creator) {
          dispatchers[alias] = (...args) => dispatch(creator(...args))
        }
      }
    }
    // Expose both as props.actions.X (Kea style) and props.X (direct) for compatibility.
    return { actions: dispatchers, ...dispatchers }
  }
}

/**
 * Registry of logics that have already been permanently mounted.
 *
 * Kea 0.28.x kept @connect-ed logics mounted as long as any consumer existed.
 * Reproducing precise reference counting across 70+ class components (with
 * react-redux render timing) is fragile, so instead we mount each referenced
 * logic exactly once on first use and keep it mounted for the app lifetime.
 * These are app-wide singleton logics (application, auth, header, ...), so the
 * memory cost is negligible and it guarantees selectors always resolve.
 */
const mountedLogics = new WeakSet()

function ensureMounted (logics) {
  for (const logic of logics) {
    if (!logic || mountedLogics.has(logic)) continue
    try {
      if (typeof logic.mount === 'function') {
        logic.mount()
        mountedLogics.add(logic)
      }
    } catch (e) {
      // ignore mount failures for already-mounted / global logics
    }
  }
}

/**
 * HOC that guarantees the referenced Kea logics are mounted before the wrapped
 * component (and therefore the inner react-redux mapStateToProps) runs.
 */
function withMountedLogics (WrappedComponent, logics) {
  if (!logics.length) return WrappedComponent

  return class WithMountedLogics extends Component {
    constructor (props) {
      super(props)
      // Mount synchronously during construction so selectors resolve on first render.
      ensureMounted(logics)
    }

    render () {
      return React.createElement(WrappedComponent, this.props)
    }
  }
}

/**
 * Detects whether a component is a React class component.
 */
function isClassComponent (Comp) {
  return !!(Comp && Comp.prototype && Comp.prototype.isReactComponent)
}

/**
 * Copy hoistable (non-React) statics from the source component to the target so
 * decorators/HOCs that read statics (e.g. propTypes, custom statics) keep working.
 */
const REACT_STATICS = {
  childContextTypes: true,
  contextType: true,
  contextTypes: true,
  defaultProps: true,
  displayName: true,
  getDefaultProps: true,
  getDerivedStateFromError: true,
  getDerivedStateFromProps: true,
  mapStateToProps: true,
  mapDispatchToProps: true,
  propTypes: true,
  type: true,
}

function hoistStatics (target, source) {
  for (const key of Object.getOwnPropertyNames(source)) {
    if (REACT_STATICS[key]) continue
    if (target[key] !== undefined) continue
    try {
      const descriptor = Object.getOwnPropertyDescriptor(source, key)
      if (descriptor) Object.defineProperty(target, key, descriptor)
    } catch (e) {
      // ignore non-configurable statics
    }
  }
  return target
}

/**
 * For class components, Kea 0.28.x exposed bound actions on the instance as
 * `this.actions` (in addition to props). Recreate that by subclassing the user
 * component and assigning `this.actions` from the injected `actions` prop before
 * the user's own lifecycle (constructor / componentDidMount) runs.
 */
function exposeActionsOnInstance (UserComponent) {
  if (!isClassComponent(UserComponent)) {
    // Function components have no `this`; they read `props.actions` directly.
    return UserComponent
  }

  class WithInstanceActions extends UserComponent {
    constructor (props, context) {
      super(props, context)
      // Make `this.actions` available for code that calls this.actions.X().
      if (props && props.actions && !this.actions) {
        this.actions = props.actions
      }
    }
  }

  // Keep statics (propTypes, displayName, custom statics) intact.
  hoistStatics(WithInstanceActions, UserComponent)
  WithInstanceActions.displayName = `KeaConnect(${UserComponent.displayName || UserComponent.name || 'Component'})`
  return WithInstanceActions
}

/**
 * Connect a class component to Kea/Redux state using the Kea 0.28.x API.
 *
 * Usage as class decorator:
 *   @connect({
 *     actions: [logicA, ['actionA']],
 *     props: [logicB, ['propB']],         // or 'values:' in migrated code
 *   })
 *   class MyComponent extends Component { ... }
 *
 * @param {Function|Object} inputFnOrObj - Kea connect spec or a function returning one
 */
export function connect (inputFnOrObj) {
  return function (WrappedComponent) {
    const spec = typeof inputFnOrObj === 'function' ? inputFnOrObj() : inputFnOrObj
    if (!spec || (!spec.actions && !spec.props && !spec.values)) {
      return WrappedComponent
    }

    const propMapping = spec.values || spec.props || []
    const actionMapping = spec.actions || []

    const mapState = buildMapState(propMapping)
    const mapDispatch = buildMapDispatch(actionMapping)

    // Expose `this.actions` for class components (Kea 0.28.x behavior).
    const Target = exposeActionsOnInstance(WrappedComponent)

    // Mount referenced logics for the component lifetime so selectors resolve.
    const logics = collectLogics(propMapping, actionMapping)
    const ConnectedInner = reduxConnect(mapState, mapDispatch)(Target)

    return withMountedLogics(ConnectedInner, logics)
  }
}
